import React, { useState, useRef, useCallback } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import { Upload as UploadIcon, Download, FileText, Trash2, Scissors, ChevronDown, ChevronUp, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { PTW_LOGO, EXACT_LOGO } from '../lib/logos';

const PURPLE = '#7c3aed';

function parseCSV(text) {
  const delimiters = [';', ',', '\t'];
  let best = null;
  for (const d of delimiters) {
    const rows = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '"') { inQuote = !inQuote; current += ch; }
      else if (ch === '\n' && !inQuote) {
        rows.push(current);
        current = '';
      } else if (ch === '\r' && !inQuote) {
        if (text[i + 1] === '\n') i++;
        rows.push(current);
        current = '';
      } else { current += ch; }
    }
    if (current.trim()) rows.push(current);
    const parsed = rows.map(r => {
      const cells = []; let cell = ''; let q = false;
      for (let i = 0; i < r.length; i++) {
        const c = r[i];
        if (c === '"') { if (q && r[i+1] === '"') { cell += '"'; i++; } else { q = !q; } }
        else if (c === d && !q) { cells.push(cell); cell = ''; }
        else { cell += c; }
      }
      cells.push(cell);
      return cells;
    });
    if (!best || (parsed.length > 1 && parsed[0].length > (best[0]?.length || 0))) best = parsed;
    if (parsed.length > 1 && parsed[0].length >= 3) { best = parsed; break; }
  }
  return best || [];
}

function rebuildCSV(header, rows, delimiter) {
  const escape = (val) => {
    const s = String(val);
    if (s.includes(delimiter) || s.includes('"') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const lines = [header.map(escape).join(delimiter)];
  for (const row of rows) {
    lines.push(row.map(escape).join(delimiter));
  }
  return lines.join('\r\n');
}

function detectDelimiter(text) {
  const first = text.split(/\r?\n/)[0] || '';
  const sc = (first.match(/;/g) || []).length;
  const cc = (first.match(/,/g) || []).length;
  const tc = (first.match(/\t/g) || []).length;
  if (sc >= cc && sc >= tc) return ';';
  if (tc >= cc) return '\t';
  return ',';
}

function downloadBlob(content, filename) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PtwImportPage() {
  const [file, setFile] = useState(null);
  const [rawText, setRawText] = useState('');
  const [header, setHeader] = useState([]);
  const [dataRows, setDataRows] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [chunkSize, setChunkSize] = useState(500);
  const [delimiter, setDelimiter] = useState(';');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const fileRef = useRef(null);

  const processFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setLoading(true);
    setError('');
    setChunks([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        setRawText(text);
        const det = detectDelimiter(text);
        setDelimiter(det);
        const parsed = parseCSV(text);
        if (parsed.length < 2) {
          setError('Bestand bevat geen data (minder dan 2 rijen)');
          setLoading(false);
          return;
        }
        const h = parsed[0];
        const dr = parsed.slice(1).filter(r => r.some(c => c.trim()));
        setHeader(h);
        setDataRows(dr);
        splitIntoChunks(dr, h, chunkSize, det, f.name);
        toast.success(`${dr.length} regels ingelezen`);
      } catch (err) {
        setError('Kon bestand niet lezen: ' + err.message);
      }
      setLoading(false);
    };
    reader.onerror = () => { setError('Bestand lezen mislukt'); setLoading(false); };
    reader.readAsText(f, 'utf-8');
  }, [chunkSize]);

  const splitIntoChunks = (rows, h, size, delim, filename) => {
    const baseName = filename.replace(/\.[^.]+$/, '');
    const parts = [];
    for (let i = 0; i < rows.length; i += size) {
      const slice = rows.slice(i, i + size);
      const partNum = Math.floor(i / size) + 1;
      const totalParts = Math.ceil(rows.length / size);
      parts.push({
        partNum,
        totalParts,
        rows: slice,
        filename: totalParts === 1 ? `PTW_deel_1.txt` : `PTW_deel_${partNum}.txt`,
        csv: rebuildCSV(h, slice, delim),
      });
    }
    setChunks(parts);
  };

  const reChunk = (newSize) => {
    const s = Math.max(1, Math.min(10000, Number(newSize) || 500));
    setChunkSize(s);
    if (dataRows.length > 0 && file) {
      splitIntoChunks(dataRows, header, s, delimiter, file.name);
    }
  };

  const downloadChunk = async (chunk) => {
    downloadBlob(chunk.csv, chunk.filename);
    toast.success(`Download: ${chunk.filename}`);
    try { await api.post('/api/ptw-import/export', { filename: chunk.filename, metadata: { rows: chunk.rows.length, part: chunk.partNum, total_parts: chunk.totalParts } }); } catch(e) { console.error('[PTW Import] Export log failed:', e); }
  };

  const downloadAll = async () => {
    for (const chunk of chunks) {
      downloadBlob(chunk.csv, chunk.filename);
      await new Promise(r => setTimeout(r, 300));
    }
    toast.success(`${chunks.length} bestanden gedownload`);
    try { await api.post('/api/ptw-import/export', { filename: `${file?.name || 'batch'}_all`, metadata: { total_rows: dataRows.length, parts: chunks.length } }); } catch(e) { console.error('[PTW Import] Export log failed:', e); }
  };

  const clear = () => {
    setFile(null); setRawText(''); setHeader([]); setDataRows([]); setChunks([]); setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="tw1-fade-in" data-testid="ptw-import-page">
      <HeroBanner
        title="PTW Fac. Import"
        subtitle="Factuurbatches splitsen voor Exact Online import"
        variant="ptw"
        icon={UploadIcon}
        logos={[{ src: PTW_LOGO, alt: 'PTW' }, { src: EXACT_LOGO, alt: 'Exact' }]}
      />

      <div className="space-y-5">
        {/* Guide Section */}
        <div className="tw1-card overflow-hidden" data-testid="ptw-guide-section">
          <button
            onClick={() => setGuideOpen(!guideOpen)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
            data-testid="ptw-guide-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${PURPLE}15` }}>
                <BookOpen className="w-4 h-4" style={{ color: PURPLE }} />
              </div>
              <span className="text-sm font-semibold text-[#0b1220]">Handleiding: PTW → Exact importeren</span>
            </div>
            {guideOpen ? <ChevronUp className="w-4 h-4 text-[#526071]" /> : <ChevronDown className="w-4 h-4 text-[#526071]" />}
          </button>
          {guideOpen && (
            <div className="px-5 pb-5 border-t border-[#e8ecf1]">
              <div className="mt-4 grid gap-5 md:grid-cols-2">
                {/* Export stappen */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: PURPLE }}>1</div>
                    <h3 className="text-sm font-semibold" style={{ color: PURPLE }}>Exporteren uit PTW</h3>
                  </div>
                  <ol className="space-y-2 ml-8">
                    {['PTW openen', 'Administratie → Exporteren', 'Exact Online selecteren', 'Batch selecteren', 'Klik "Exporteren"', 'CSV-bestand opslaan'].map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#3a4553]">
                        <span className="text-xs font-mono mt-0.5 min-w-[18px]" style={{ color: PURPLE }}>{i + 1}.</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
                {/* Import stappen */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: PURPLE }}>2</div>
                    <h3 className="text-sm font-semibold" style={{ color: PURPLE }}>Importeren in Exact</h3>
                  </div>
                  <ol className="space-y-2 ml-8">
                    {[
                      'Exact → TW1 Group administratie',
                      'Import/Export → Verkoopboekingen',
                      'Bestandstype: Excel 97/2003',
                      'Bestand kiezen (gesplitst deel)',
                      'Maandafsluiting → Nee',
                      'Aan veranderde waarde',
                      '3× Volgende klikken',
                      'Vakje "Uitsluiten" aanvinken',
                      'Klaar',
                    ].map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#3a4553]">
                        <span className="text-xs font-mono mt-0.5 min-w-[18px]" style={{ color: PURPLE }}>{i + 1}.</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg text-xs text-[#526071]" style={{ background: `${PURPLE}08` }}>
                <strong>Tip:</strong> Exact kan max ~500 regels per keer verwerken. Gebruik de splitter hieronder om grote batches op te delen.
              </div>
            </div>
          )}
        </div>

        {/* Upload */}
        {!file ? (
          <div
            className="tw1-dropzone p-8 text-center"
            onClick={() => fileRef.current?.click()}
            data-testid="ptw-upload-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={e => processFile(e.target.files?.[0])}
              data-testid="ptw-file-input"
            />
            <UploadIcon className="w-10 h-10 mx-auto mb-3" style={{ color: PURPLE }} />
            <p className="text-sm font-medium text-[#0b1220]">Sleep CSV-bestand hierheen</p>
            <p className="text-xs text-[#526071] mt-1">of klik om te bladeren (.csv)</p>
          </div>
        ) : (
          <div className="tw1-card p-4 flex items-center justify-between" data-testid="ptw-file-info">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5" style={{ color: PURPLE }} />
              <div>
                <p className="text-sm font-medium text-[#0b1220]">{file.name}</p>
                <p className="text-xs text-[#526071]">{(file.size / 1024).toFixed(1)} KB — {dataRows.length} regels</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={clear} data-testid="ptw-clear-btn">
              <Trash2 className="w-4 h-4 text-gray-400" />
            </Button>
          </div>
        )}

        {loading && (
          <div className="tw1-card p-8 text-center">
            <div className="animate-spin w-8 h-8 border-3 rounded-full mx-auto" style={{ borderColor: `${PURPLE}30`, borderTopColor: PURPLE }} />
            <p className="text-sm text-[#526071] mt-3">Verwerken...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-[#c0392b]" data-testid="ptw-error">{error}</div>
        )}

        {/* Chunk Size Config + Results */}
        {chunks.length > 0 && (
          <>
            {/* Config bar */}
            <div className="tw1-card p-4 flex flex-wrap items-center justify-between gap-4" data-testid="ptw-config-bar">
              <div className="flex items-center gap-3">
                <Scissors className="w-4 h-4" style={{ color: PURPLE }} />
                <span className="text-sm text-[#3a4553]">Max regels per deel:</span>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={chunkSize}
                  onChange={e => reChunk(e.target.value)}
                  className="w-20 h-8 border border-[#e0e4ea] rounded-lg px-2 text-sm text-center focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': `${PURPLE}40` }}
                  data-testid="ptw-chunk-size-input"
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-[#526071]">
                <span><strong className="text-[#0b1220]">{dataRows.length}</strong> regels</span>
                <ArrowRight className="w-3 h-3" />
                <span><strong className="text-[#0b1220]">{chunks.length}</strong> {chunks.length === 1 ? 'bestand' : 'bestanden'}</span>
              </div>
            </div>

            {/* Download All */}
            {chunks.length > 1 && (
              <Button
                onClick={downloadAll}
                className="w-full h-11 text-sm font-semibold text-white rounded-xl"
                style={{ background: PURPLE }}
                data-testid="ptw-download-all-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download alle {chunks.length} bestanden
              </Button>
            )}

            {/* Chunk Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ptw-chunks-grid">
              {chunks.map((chunk) => (
                <div key={chunk.partNum} className="tw1-card p-4 flex items-center justify-between" data-testid={`ptw-chunk-${chunk.partNum}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${PURPLE}12` }}>
                      <span className="text-xs font-bold" style={{ color: PURPLE }}>{chunk.partNum}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0b1220] truncate">{chunk.filename}</p>
                      <p className="text-xs text-[#526071]">{chunk.rows.length} regels</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadChunk(chunk)}
                    className="shrink-0"
                    data-testid={`ptw-download-chunk-${chunk.partNum}`}
                  >
                    <Download className="w-4 h-4" style={{ color: PURPLE }} />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
